import React from 'react';
import isEqual from 'lodash.isequal';
import { Dispatch } from 'redux';
import { AppState, Booking, BookingsFilter, filterBookingsOnOwnerId, filterBookingsOnType } from 'acscp/store';
import { BookingsRequest, FlownBookingsRequest, RequestClass } from 'acscp/api/requests';
import { FetchRequest } from 'acscp/actions';
import bookingsComparison from 'acscp/store/helpers/sortBookings';
import { BookingsList } from 'acscp/components/bookings/bookingsList';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { FormattedMessage } from 'react-intl';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import style from './bookingsLists.module.scss';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { getDomIdForFilter } from '../common/helpers/getDomIDs';
import { PageHeaderRow } from 'acscp/components/common/pageHeaderRow';
import { HashLink } from 'react-router-hash-link';
import classNames from 'classnames';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';

const messages = defineMessages({
    allCharters: {
        id: 'components.bookings.bookingsLists.allCharters',
        defaultMessage: 'All Charters'
    },
    myCharters: {
        id: 'components.bookings.bookingsLists.myCharters',
        defaultMessage: 'My Charters'
    }
});

type FlownBookingsListsOwnProps = InjectedIntlProps;

interface FlownBookingsListsDispatchProps {
    fetchBookings: () => void;
}

interface FlownBookingsListsStateProps extends RequestStateProps {
    isLoggedIn: boolean;
    flownBookings: Booking[];
}

interface PageState {
    categoryFilterId: string;
}

export class FlownBookingsListsPresentational
    extends PresentationComponentBase<FlownBookingsListsOwnProps,
    FlownBookingsListsStateProps,
    FlownBookingsListsDispatchProps,
    PageState> {

    constructor(props: {} & FlownBookingsListsStateProps & FlownBookingsListsOwnProps & FlownBookingsListsDispatchProps) {
        super(props);
        this.state = {
            categoryFilterId: '' // default to no filtering
        };
    }

    componentDidMount(): void {
        this.props.fetchBookings();
    }

    // React is only performing a shallow comparison of the props.
    // This is causing React to think that arrays in the props are new and so re-renders everything,
    // whereas they often have the same contents. We perform a deep comparison to check.
    shouldComponentUpdate(nextProps: FlownBookingsListsStateProps & FlownBookingsListsDispatchProps, nextState: PageState): boolean {
        return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
    }

    getBookingOwnerIdFilterOptions(allBookings: Booking[]): JSX.Element[] {
        let categoryOptions: JSX.Element[] = [];

        const firstBookingOwnedByCurrentUser = allBookings.find(value => value.ownedByCurrentUser);
        if (firstBookingOwnedByCurrentUser !== undefined) {
            categoryOptions = categoryOptions.concat((
                <option key={firstBookingOwnedByCurrentUser.ownerOfBookingId} value={firstBookingOwnedByCurrentUser.ownerOfBookingId}>
                    {this.props.intl.formatMessage(messages.myCharters)}
                </option>
            ));
        }

        categoryOptions = categoryOptions.concat(allBookings
            .filter((value, index, self) => {
                return !value.ownedByCurrentUser &&
                    self.map(b => b.ownerOfBookingId).indexOf(value.ownerOfBookingId) === index;
            })

            .sort((a, b) => a.ownerOfBookingName.localeCompare(b.ownerOfBookingName))
            .map(booking => (<option key={booking.ownerOfBookingId} value={booking.ownerOfBookingId}>{booking.ownerOfBookingName}</option>)
            ));

        if (categoryOptions.length > 1) {
            categoryOptions = [(
                <option key={''} value={''}>
                    {this.props.intl.formatMessage(messages.allCharters)}
                </option>
            ), ...categoryOptions];
        }

        return categoryOptions;
    }

    render(): JSX.Element {
        if (this.props.isLoading) {
            return (
                <div className={style.Loading}>
                    <LoadingIndicator />
                </div>
            );
        }

        const categoryOptions = this.getBookingOwnerIdFilterOptions(this.props.flownBookings);

        const flownBookingsToRender = filterBookingsOnOwnerId(this.props.flownBookings, this.state.categoryFilterId)
            .filter(value => this.props.isLoggedIn || !value.ownedByCurrentUser);

        return (
            <div>
                <PageHeaderRow
                    leftContent={(
                        <p className={style.HeaderRowTitle}>
                            <FormattedMessage
                                id={'components.bookings.bookingsLists.listOfFlownEnquiries'}
                                defaultMessage={'FLOWN CHARTERS'}
                            />
                        </p>
                    )}
                    showLoadingIndicator={false}
                    rightContent={categoryOptions.length > 1 ? (
                        <p className={style.FilterContainer}>
                            <FormattedMessage
                                id={'components.bookings.bookingsLists.filterChartersLabel'}
                                defaultMessage={'Filter'}
                            />
                            <select
                                onChange={v => this.setState({categoryFilterId: v.target.value})}
                                className={style.Filter}
                            >
                                {categoryOptions}
                            </select>
                        </p>
                    ) : null}
                />
                <PageHeaderRow
                    leftContent={(
                        <HashLink className={classNames(style.HeaderRowTitle, style.UpcomingChartersButton)} to={'/trip'}>
                            <ArrowIcon />
                            <FormattedMessage
                                id={'components.bookings.bookingsLists.listOfUpcomingEnquiries'}
                                defaultMessage={'UPCOMING CHARTERS'}
                            />
                        </HashLink>
                    )}
                    showLoadingIndicator={false}
                    rightContent={null}
                />

                <BookingsList
                    id={getDomIdForFilter(BookingsFilter.Flown)}
                    bookingsFilter={BookingsFilter.Flown}
                    bookings={flownBookingsToRender}
                />
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): FlownBookingsListsStateProps {
    const requestStateProps = getRequestError(BookingsRequest);

    return {
        ...requestStateProps,
        isLoggedIn: userIsLoggedIn(state.auth),
        flownBookings: filterBookingsOnType(state.bookingsDetails, BookingsFilter.Flown).sort(bookingsComparison),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): FlownBookingsListsDispatchProps {
    return {
        fetchBookings: () => dispatch(new FetchRequest(new FlownBookingsRequest()))
    };
}

export const FlownBookingsLists = connectWithRequests(mapStateToProps, mapDispatchToProps)
(injectIntl(FlownBookingsListsPresentational));
